import React from 'react';
import './PortfolioSummaryToolbar.scss';
import { useDispatchUpdateWidgetConfiguration } from 'widgets/store/configuration/hooks';
import {
    PortfolioSummaryConfigurationState,
    PortfolioSummarySideNetOptions,
} from '../store/instances/configuration/PortfolioSummaryConfigurationState';
import { usePortfolioSummaryConfigurationSelector } from '../store/instances/configuration/selectors';
import {
    createGridChartTreemapViewToggleButtons,
    GridChartTreemapView,
} from 'utils/components/ViewToggleButtons/ViewToggleButtons';
import classNames from 'classnames';
import { useSelectIsAppModeNewportSingleWidget } from 'app/store/general/selectors';
import AggregationFieldDropdown from './AggregationFieldDropdown';
import {
    Dropdown,
    ToolbarIcon,
} from '@instinet-analytics/semantic-ui-components';
import { PubSub } from '@instinet-analytics/core-lib';
import styleConstants from 'styles/variables.module.scss';
import PortfolioSummaryChartHelp from '../chart/help/PortfolioSummaryChartHelp';
import AppSettingsEditor from 'appToolbar/AppSettingsEditor';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { useDispatchUpdateAppSettings } from 'app/store/appSettings/dispatcher';
import PortfolioSummaryColumnPicker from '../grid/PortfolioSummaryColumnPicker';
import WidgetToolbar from 'widgets/WidgetToolbar/WidgetToolbar';
import PortfolioSummaryTreemapHelp from '../treemap/help/PortfolioSummaryTreemapHelp';
import WidgetToolbarShowHidePanel from 'widgets/WidgetToolbarShowHidePanel/WidgetToolbarShowHidePanel';
import { useWidgetContext } from 'widgets/WidgetContext';
import { QuantitiesOptions } from 'utils/components/QuantitiesOptions/QuanitiesOptions';
import QuantitiesOptionsLinks from 'utils/components/QuantitiesOptions/QuantitiesOptionsLinks';
import { GridApi } from '@instinet-analytics/semantic-ui-components';
import { GridExportButton } from '@instinet-analytics/semantic-ui-components';
import { usePortfolioActiveInputUsesOrdersStream } from 'portfolio/store/hooks';
import { PortfolioSummaryAddFilterButton } from 'widgets/portfolioSummary/toolbar/PortfolioSummaryAddFilterButton';
import ActiveFiltersIndicator from 'appToolbar/ActiveFiltersIndicator';
import { useWidgetEditedColumnNames } from 'grid/columnNames/hooks';

export interface PortfolioSummaryToolbarProps {
    gridApi?: GridApi;
    title: string;
    selectedValues: string[];
    changedFilterRef: PubSub<void>;
}

export default function PortfolioSummaryToolbar(
    props: PortfolioSummaryToolbarProps
) {
    const { changedFilterRef, gridApi, title, selectedValues } = props;
    const { width } = useWidgetContext();

    const config = usePortfolioSummaryConfigurationSelector();
    const { aggregationField, view, quantities, sideNet } = config;
    const appSettings = useSelectAppSettings();
    const dispatchUpdateAppSettings = useDispatchUpdateAppSettings();
    const { editedColumnNames } = useWidgetEditedColumnNames();

    const dispatchWidgetConfigUpdate =
        useDispatchUpdateWidgetConfiguration<PortfolioSummaryConfigurationState>();

    const isAppModeNewportSingleWidget =
        useSelectIsAppModeNewportSingleWidget();

    const onViewChanged = (view: GridChartTreemapView) => {
        dispatchWidgetConfigUpdate({ view });
    };

    const onQuantitiesChanged = (quantities: QuantitiesOptions) => {
        dispatchWidgetConfigUpdate({ quantities });
    };

    const onSideNetChanged = (sideNet: PortfolioSummarySideNetOptions) => {
        dispatchWidgetConfigUpdate({ sideNet });
    };

    const onSideClicked = () => onSideNetChanged('side');
    const onNetClicked = () => onSideNetChanged('net');

    const activeInputUsesOrdersStream =
        usePortfolioActiveInputUsesOrdersStream();

    const NewportWidthAdj = isAppModeNewportSingleWidget
        ? parseInt(styleConstants.iconSize)
        : 0;
    const FullWidth = 410 - NewportWidthAdj;
    const MediumWidth = 320 - NewportWidthAdj;

    const isFullWidth = width >= FullWidth;

    const isMediumWidth = width >= MediumWidth;

    const csvFileName = title !== '' ? title : 'Portfolio Summary';

    return (
        <WidgetToolbarShowHidePanel mustShow={false}>
            <div className="PortfolioSummaryToolbar panel-header">
                <div className="toolbar">
                    <AggregationFieldDropdown value={aggregationField} />
                    {view === 'chart' && renderSideNetLinksOrDropdown()}
                    {(view === 'chart' || view === 'treemap') &&
                        renderQuantitiesLinksOrDropdown()}
                    {renderHelp(view)}
                </div>

                <WidgetToolbar
                    fixedItems={
                        view === 'grid' ? (
                            <>
                                <PortfolioSummaryAddFilterButton
                                    changedFilterRef={changedFilterRef}
                                    selectedValues={selectedValues}
                                    aggregationField={aggregationField}
                                />
                                <ActiveFiltersIndicator
                                    options={{
                                        column: aggregationField,
                                        element: (
                                            <div className="BlotterFiltersModalTrigger">
                                                <ToolbarIcon name="filter" />
                                                <div className="filter-indicator" />
                                            </div>
                                        ),
                                    }}
                                />
                                {activeInputUsesOrdersStream && (
                                    <GridExportButton
                                        csvFileName={csvFileName}
                                        gridApi={gridApi}
                                        editedColumnNames={editedColumnNames}
                                    />
                                )}
                                <PortfolioSummaryColumnPicker
                                    aggregationField={aggregationField}
                                />
                            </>
                        ) : undefined
                    }
                    collapsibleItems={createGridChartTreemapViewToggleButtons(
                        view,
                        onViewChanged
                    )}
                    settingsHeader="Portfolio Summary Settings"
                    settingsBody={
                        isAppModeNewportSingleWidget && view !== 'grid' ? (
                            <div className="PortfolioSummarySettings">
                                <AppSettingsEditor
                                    appSettings={appSettings}
                                    onAppSettingsChanged={
                                        dispatchUpdateAppSettings
                                    }
                                    specifyVisibleControls={{
                                        showChartHelp: true,
                                    }}
                                />
                            </div>
                        ) : undefined
                    }
                />
            </div>
        </WidgetToolbarShowHidePanel>
    );

    function renderQuantitiesLinksOrDropdown() {
        const showLinks = isFullWidth;
        return (
            <>
                <div className="separator" />

                {showLinks && (
                    <QuantitiesOptionsLinks
                        value={quantities}
                        onChange={onQuantitiesChanged}
                    />
                )}

                {!showLinks && (
                    <div className="toolbar-dropdown quantities">
                        <Dropdown
                            options={[
                                { text: 'Traded', value: 'traded' },
                                { text: 'Leaves', value: 'leaves' },
                                { text: 'Ordered', value: 'ordered' },
                            ]}
                            value={quantities}
                            onChange={onQuantitiesChanged}
                        />
                    </div>
                )}
            </>
        );
    }

    function renderSideNetLinksOrDropdown() {
        const showLinks = isMediumWidth;

        return (
            <>
                <div className="separator" />

                {showLinks && (
                    <div className="toolbar-links side-net">
                        <div
                            className={classNames(
                                'text-link',
                                sideNet === 'side' && 'active'
                            )}
                            onClick={onSideClicked}
                        >
                            Side
                        </div>
                        <div
                            className={classNames(
                                'text-link',
                                sideNet === 'net' && 'active'
                            )}
                            onClick={onNetClicked}
                        >
                            Net
                        </div>
                    </div>
                )}

                {!showLinks && (
                    <div className="toolbar-dropdown dropdown-side-net">
                        <Dropdown
                            options={[
                                { text: 'Side', value: 'side' },
                                { text: 'Net', value: 'net' },
                            ]}
                            value={sideNet}
                            onChange={onSideNetChanged}
                        />
                    </div>
                )}
            </>
        );
    }

    function renderHelp(view: GridChartTreemapView) {
        switch (view) {
            case 'chart':
                return <PortfolioSummaryChartHelp />;
            case 'treemap':
                return <PortfolioSummaryTreemapHelp />;
            default:
                return <></>;
        }
    }
}
